import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PencilLine } from 'lucide-react';
import { useAuthStore } from '../stores/authStore.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useTopBarStore } from '../stores/topBarStore.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { Segmented, SegmentedOption } from '../components/ui/Segmented.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { Button } from '../components/ui/Button.js';
import { Notice } from '../components/ui/Notice.js';
import { useToast } from '../components/ui/ToastContext.js';
import { EditKindChip } from '../components/tabEdits/EditKindChip.js';
import { EditStatusBadge } from '../components/tabEdits/EditStatusBadge.js';
import { db } from '../db/index.js';
import {
  getMyEditsList,
  markDecisionsSeen,
  dropEdit,
  markWithdrawPending
} from '../db/tabEdits.js';
import { syncTabEdits, requestTabEditSync } from '../sync/tabEditSync.js';
import { TAB_EDITS_CHANGED_EVENT } from '../sync/events.js';
import { TAB_EDIT_COPY } from '../utils/tabEdits.js';
import { formatPersianRelativeDate, toPersianDigits } from '../utils/timeUtils.js';
import type { LocalTabEdit, TabSong, TabEditStatus } from '../types/index.js';

type StatusFilter = 'all' | TabEditStatus;

export const MyEditsPage: React.FC = () => {
  usePageTitle(TAB_EDIT_COPY.c70Title);

  const navigate = useNavigate();
  const toast = useToast();
  const { user, isGuest, isAuthenticated, openVerifyDialog } = useAuthStore();
  const { setTopBarConfig, resetTopBarConfig } = useTopBarStore();

  const [edits, setEdits] = useState<LocalTabEdit[]>([]);
  const [tabMap, setTabMap] = useState<Record<string, TabSong | null>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<StatusFilter>('all');

  // Protected: if guest or not authenticated, redirect to /auth
  useEffect(() => {
    if (!isAuthenticated || isGuest) {
      navigate('/auth', { replace: true });
    }
  }, [isAuthenticated, isGuest, navigate]);

  // Mobile TopBar setup
  useEffect(() => {
    setTopBarConfig({
      title: TAB_EDIT_COPY.c70Title,
      showBack: true,
      onBack: () => navigate(-1)
    });
    return () => {
      resetTopBarConfig();
    };
  }, [setTopBarConfig, resetTopBarConfig, navigate]);

  // Load edits and tab metadata from Dexie
  const loadEdits = useCallback(async () => {
    if (!user?.id || isGuest) return;
    try {
      const items = await getMyEditsList(user.id);
      setEdits(items);

      // Fetch corresponding tabs
      const uniqueTabIds = Array.from(new Set(items.map((e) => e.tabId).filter(Boolean)));
      if (uniqueTabIds.length > 0) {
        const fetchedTabs = await Promise.all(uniqueTabIds.map((id) => db.tabs.get(id)));
        const map: Record<string, TabSong | null> = {};
        uniqueTabIds.forEach((id, idx) => {
          map[id] = fetchedTabs[idx] ?? null;
        });
        setTabMap(map);
      }
    } catch (err) {
      console.error('Failed to load user edits:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, isGuest]);

  // Initial load: mark decisionSeen = 1, load edits, trigger sync if online
  useEffect(() => {
    if (!user?.id || isGuest) return;

    let active = true;

    const init = async () => {
      // Mark all unseen decisions as seen so AccountMenu dot clears
      try {
        await markDecisionsSeen(user.id);
      } catch (err) {
        console.error('Failed to mark decisions as seen:', err);
      }

      if (active) {
        await loadEdits();
      }

      // Background sync if online
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        syncTabEdits()
          .then(async () => {
            if (active && user?.id) {
              await markDecisionsSeen(user.id);
              await loadEdits();
            }
          })
          .catch((err) => {
            console.warn('Background syncTabEdits failed on MyEditsPage:', err);
          });
      }
    };

    void init();

    const handleChanged = () => {
      if (active) void loadEdits();
    };

    window.addEventListener(TAB_EDITS_CHANGED_EVENT, handleChanged);

    return () => {
      active = false;
      window.removeEventListener(TAB_EDITS_CHANGED_EVENT, handleChanged);
    };
  }, [user?.id, isGuest, loadEdits]);

  // Withdraw edit handler
  const handleWithdraw = async (edit: LocalTabEdit) => {
    // Optimistically remove from state
    setEdits((prev) => prev.filter((e) => e.id !== edit.id));

    if (edit.status === 'QUEUED') {
      await dropEdit(edit.id);
      toast.undo(TAB_EDIT_COPY.c06, async () => {
        await db.tabEdits.put(edit);
        setEdits((prev) =>
          [edit, ...prev].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        );
      });
    } else {
      // PENDING
      await markWithdrawPending(edit.id);
      let undone = false;
      toast.undo(TAB_EDIT_COPY.c06, async () => {
        undone = true;
        await db.tabEdits.update(edit.id, { pendingWithdraw: 0 });
        setEdits((prev) =>
          [edit, ...prev].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        );
      });

      // Schedule sync push after undo window
      window.setTimeout(() => {
        if (!undone) {
          requestTabEditSync();
        }
      }, 4000);
    }
  };

  const pendingCount = useMemo(
    () => edits.filter((e) => e.status === 'PENDING' || e.status === 'QUEUED').length,
    [edits]
  );

  const filterOptions: SegmentedOption<StatusFilter>[] = useMemo(
    () => [
      { value: 'all', label: 'همه' },
      {
        value: 'PENDING',
        label: 'در انتظار',
        count: pendingCount > 0 ? toPersianDigits(pendingCount) : undefined
      },
      { value: 'APPROVED', label: 'تأییدشده' },
      { value: 'REJECTED', label: 'ردشده' }
    ],
    [pendingCount]
  );

  const filteredEdits = useMemo(() => {
    if (activeFilter === 'all') return edits;
    if (activeFilter === 'PENDING') {
      return edits.filter((e) => e.status === 'PENDING' || e.status === 'QUEUED');
    }
    return edits.filter((e) => e.status === activeFilter);
  }, [edits, activeFilter]);

  if (!isAuthenticated || isGuest) {
    return null;
  }

  return (
    <div data-testid="my-edits-page" className="flex flex-col gap-4 lg:gap-5 max-w-[1120px] mx-auto">
      {/* Header (desktop only; mobile uses the TopBar title + back) */}
      <PageHeader
        className="max-lg:hidden"
        title={TAB_EDIT_COPY.c70Title}
        onBack={() => navigate(-1)}
      />

      {/* Filter Segmented (only when there are edits) */}
      {edits.length > 0 && (
        <div className="w-full lg:w-auto lg:self-start">
          <Segmented<StatusFilter>
            options={filterOptions}
            value={activeFilter}
            onChange={(val) => setActiveFilter(val)}
            className="w-full lg:w-auto grid grid-cols-4 lg:inline-flex gap-0.5 [&_button]:whitespace-nowrap [&_button]:px-1.5 lg:[&_button]:px-3.5"
          />
        </div>
      )}

      {/* Content Area */}
      {isLoading ? (
        <div className="flex flex-col gap-3">
          <div className="h-24 rounded-2xl bg-base-200 animate-pulse" />
          <div className="h-24 rounded-2xl bg-base-200 animate-pulse" />
          <div className="h-24 rounded-2xl bg-base-200 animate-pulse" />
        </div>
      ) : edits.length === 0 ? (
        <EmptyState
          data-testid="my-edits-empty"
          icon={<PencilLine className="size-6" />}
          title="هنوز پیشنهادی برای ویرایش تب‌ها ثبت نکرده‌اید."
          description={TAB_EDIT_COPY.c70EmptyDescription}
          action={
            <Button variant="secondary" onClick={() => navigate('/tabs')}>
              رفتن به کتابخانه
            </Button>
          }
        />
      ) : filteredEdits.length === 0 ? (
        <EmptyState
          data-testid="my-edits-filter-empty"
          icon={<PencilLine className="size-6" />}
          title="موردی با این فیلتر وجود ندارد."
          action={
            <Button variant="secondary" onClick={() => setActiveFilter('all')}>
              همه
            </Button>
          }
        />
      ) : (
        <div data-testid="my-edits-list" className="flex flex-col gap-3">
          {filteredEdits.map((edit) => {
            const tab = edit.tabId ? tabMap[edit.tabId] : null;
            const isTabDeleted = !edit.tabId || (!tab && !edit.tabTitle);
            const tabTitle = tab?.title || edit.tabTitle || 'تب حذف‌شده';
            const tabArtist = tab?.artist;
            const isPendingOrQueued = edit.status === 'PENDING' || edit.status === 'QUEUED';

            return (
              <div
                key={edit.id}
                data-testid={`my-edit-row-${edit.id}`}
                className="rounded-2xl border border-base-300 bg-base-100 p-4 lg:px-5 lg:py-4 flex flex-col gap-3 transition-colors"
              >
                {/* Row Header: Kind chip, Timestamp, Status badge */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <EditKindChip kind={edit.kind} />
                    <span className="text-xs text-base-content/60 font-medium">
                      {formatPersianRelativeDate(edit.createdAt)}
                    </span>
                  </div>
                  <EditStatusBadge status={edit.status} />
                </div>

                {/* Tab Title & Artist */}
                <div className="flex items-center gap-2 min-w-0">
                  {isTabDeleted ? (
                    <span className="font-bold text-base text-base-content/70 truncate">
                      <bdi dir="auto">{tabTitle}</bdi>
                    </span>
                  ) : (
                    <Link
                      to={`/tab/${edit.tabId}`}
                      data-testid={`my-edit-tab-link-${edit.id}`}
                      className="font-bold text-base text-base-content hover:text-primary transition truncate"
                    >
                      <bdi dir="auto">{tabTitle}</bdi>
                    </Link>
                  )}
                  {tabArtist && (
                    <span className="text-xs text-base-content/60 truncate" dir="auto">
                      · {tabArtist}
                    </span>
                  )}
                </div>

                {/* Note line if present */}
                {edit.note && (
                  <p className="text-xs text-base-content/80 line-clamp-2" dir="auto">
                    «{edit.note}»
                  </p>
                )}

                {/* Reject Reason line */}
                {edit.status === 'REJECTED' && (
                  <p
                    data-testid={`my-edit-reason-${edit.id}`}
                    className="text-xs text-error-soft font-medium"
                  >
                    {edit.rejectReason
                      ? `دلیل رد: ${edit.rejectReason}`
                      : 'ردشده'}
                  </p>
                )}

                {/* Unverified Email Notice */}
                {edit.blocked === 'unverified' && (
                  <Notice
                    tone="warning"
                    action={
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openVerifyDialog()}
                      >
                        {TAB_EDIT_COPY.c21Action}
                      </Button>
                    }
                  >
                    {TAB_EDIT_COPY.c21}
                  </Notice>
                )}

                {/* Actions Row */}
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-base-200 mt-0.5">
                  <div>
                    {!isTabDeleted && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/tab/${edit.tabId}`)}
                        data-testid={`my-edit-open-${edit.id}`}
                      >
                        {TAB_EDIT_COPY.c71ViewTab}
                      </Button>
                    )}
                  </div>

                  <div>
                    {isPendingOrQueued && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-error-soft hover:bg-error/10"
                        onClick={() => handleWithdraw(edit)}
                        data-testid={`my-edit-withdraw-${edit.id}`}
                      >
                        {TAB_EDIT_COPY.c71Withdraw}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

MyEditsPage.displayName = 'MyEditsPage';
export default MyEditsPage;
